import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, UserCog, ShieldOff, ShieldCheck } from "lucide-react";

const ROLES = ["borrower", "dealer", "dsa", "underwriter", "operations", "collections", "admin", "field_agent", "valuation_agent"];

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/users", { params: { q: q || undefined, role: role || "all" } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const startCreate = () => { setEditing(null); setOpen(true); };
  const startEdit = (u) => { setEditing(u); setOpen(true); };

  const toggleDisable = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/toggle-disable`);
      toast.success(`${u.email} ${u.disabled ? "enabled" : "disabled"}`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-600 mt-1">{items.length} user{items.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2 items-center">
          <form onSubmit={e => { e.preventDefault(); load(); }}>
            <Input data-testid="users-search" placeholder="Search by email, name or org" value={q} onChange={e => setQ(e.target.value)} className="w-64" />
          </form>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-44" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={startCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="users-create-btn"><Plus size={16} className="mr-1" /> New User</Button>
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Organization</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-700 font-mono">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.role?.replace("_", " ")}</td>
                <td className="px-4 py-3 text-slate-600">{u.organization || "—"}</td>
                <td className="px-4 py-3">
                  <span className={"inline-flex px-2 py-0.5 rounded-full text-xs border " + (u.disabled ? "badge-rejected" : "badge-approved")}>
                    {u.disabled ? "Disabled" : "Active"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(u)} data-testid={`user-edit-${u.email}`}><UserCog size={14} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleDisable(u)} data-testid={`user-toggle-${u.email}`}>
                    {u.disabled ? <><ShieldCheck size={14} className="mr-1" /> Enable</> : <><ShieldOff size={14} className="mr-1" /> Disable</>}
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No users.</td></tr>}
          </tbody>
        </table>
      </Card>

      <UserDialog open={open} setOpen={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} busy={busy} setBusy={setBusy} />
    </div>
  );
}

function UserDialog({ open, setOpen, editing, onSaved, busy, setBusy }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "borrower", phone: "", organization: "" });
  useEffect(() => {
    if (editing) setForm({ email: editing.email, password: "", name: editing.name || "", role: editing.role, phone: editing.phone || "", organization: editing.organization || "" });
    else setForm({ email: "", password: "", name: "", role: "borrower", phone: "", organization: "" });
  }, [editing, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone, organization: form.organization };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit user" : "Create user"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Email</Label>
            <Input data-testid="user-form-email" value={form.email} disabled={!!editing} onChange={e => set("email", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Name</Label>
            <Input data-testid="user-form-name" value={form.name} onChange={e => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={v => set("role", v)}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="user-form-phone" value={form.phone} onChange={e => set("phone", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Organization (dealer/DSA only)</Label>
            <Input data-testid="user-form-org" value={form.organization} onChange={e => set("organization", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label>
            <Input data-testid="user-form-password" type="password" value={form.password} onChange={e => set("password", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="user-form-save-btn">{editing ? "Save changes" : "Create user"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
